import { get } from 'lodash-es';
import { CARE_ORDER_BABY_TYPE } from '@/interface/domain/careOrderBaby';
import useEffect from '@/util/hook/useEffect';
import useSelector from '@/util/hook/useSelector';
import useState from '@/util/hook/useState';
import { selectLiffId } from '@/util/redux/auth';
import { orderAPI } from '@/api/careClient';
import { selectCareOrderBabyForm, } from '../../redux/module/careOrderBabyForm';
import { selectPartTimeCareOrderPlanList } from '../../redux/module/remoteConfig';
import { CARE_ORDER_PLAN_DESCRIPTION, CARE_ORDER_PLAN_KEY, PART_TIME_CARE_ORDER_PLAN_KEY_HOUR_MAP, } from '../../config/careOrderPlan';
const STROKE_SYMPTOM_TITLE = '中風';
const DISPLAY_TITLE_PREFIX_FOR_STROKE_SYMPTOM = '中風';
/**
 * @description
 * Customize the careOrderPlanOption display contents for different careOrderBaby needs
 * such as the '中風' symptom
 */
const getCustomizedCareOrderPlanOption = ({ careOrderPlanOption, symptom, }) => {
    const isStroke = symptom.includes(STROKE_SYMPTOM_TITLE);
    const customizedCareOrderPlanOption = {
        ...careOrderPlanOption,
        displayTitle: isStroke
            ? `${DISPLAY_TITLE_PREFIX_FOR_STROKE_SYMPTOM}${careOrderPlanOption.displayTitle}`
            : careOrderPlanOption.displayTitle,
    };
    return customizedCareOrderPlanOption;
};
const getAllDayCareOrderPlanOptionList = ({ allDayCareOrderDailyFee, symptom, }) => {
    const careOrderPlanOption = {
        key: CARE_ORDER_PLAN_KEY.ALL_DAY,
        displayTitle: '全天候照顧',
        parsedFee: `預估 $${allDayCareOrderDailyFee.toLocaleString()} / 天`,
        description: CARE_ORDER_PLAN_DESCRIPTION.ALL_DAY,
        initialType: CARE_ORDER_BABY_TYPE.ALL_DAY,
        hour: 24,
    };
    const customizedCareOrderPlanOption = getCustomizedCareOrderPlanOption({
        careOrderPlanOption,
        symptom,
    });
    const allDayCareOrderPlanOptionList = [customizedCareOrderPlanOption];
    return allDayCareOrderPlanOptionList;
};
const getPartTimeCareOrderPlanOptionList = ({ partTimeCareOrderPlanList, partTimeCareOrderHourlyFee, symptom, }) => {
    const partTimeCareOrderPlanOptionList = partTimeCareOrderPlanList.map((careOrderPlan) => {
        const careOrderPlanKey = CARE_ORDER_PLAN_KEY[PART_TIME_CARE_ORDER_PLAN_KEY_HOUR_MAP[careOrderPlan]];
        const careOrderPlanOption = {
            key: careOrderPlanKey,
            displayTitle: `喘息支援 - ${careOrderPlan}小時`,
            parsedFee: `預估 $${partTimeCareOrderHourlyFee.toLocaleString()} / 小時`,
            description: CARE_ORDER_PLAN_DESCRIPTION[careOrderPlanKey],
            initialType: CARE_ORDER_BABY_TYPE.PART_TIME,
            hour: careOrderPlan,
        };
        const customizedCareOrderPlanOption = getCustomizedCareOrderPlanOption({
            careOrderPlanOption,
            symptom,
        });
        return customizedCareOrderPlanOption;
    });
    return partTimeCareOrderPlanOptionList;
};
const isMockApiMode = (process.env.ENV.TARGET_ENV === 'local' || process.env.ENV.TARGET_ENV === 'dev') &&
    process.env.ENV.TEST_SCENARIO;
const fetchAllDayCareOrderTimeSlotFee = ({ _id, careOrderData, setApiProcessingPool, setApiDonePool, }) => orderAPI.TimeSlotFeeCalculate({
    _id,
    careOrderId: null,
    careOrderData: { ...careOrderData, initialType: CARE_ORDER_BABY_TYPE.ALL_DAY },
    timeSlot: {
        expectedServiceHours: null,
        expectedServiceDays: 1,
    },
    ...(isMockApiMode && {
        initialType: CARE_ORDER_BABY_TYPE.ALL_DAY,
    }),
}, setApiProcessingPool, setApiDonePool);
const fetchPartTimeCareOrderTimeSlotFee = ({ _id, careOrderData, partTimeCareOrderPlanList, setApiProcessingPool, setApiDonePool, }) => orderAPI.TimeSlotFeeCalculate({
    _id,
    careOrderId: null,
    careOrderData: { ...careOrderData, initialType: CARE_ORDER_BABY_TYPE.PART_TIME },
    timeSlot: {
        // #NOTES
        // In this page, we only display the 'hourlyFee' of each partTime careOrderPlan.
        // For every partTime careOrderPlan having the same 'hourlyFee',
        // we choose the first partTime careOrderPlan to calculate
        expectedServiceHours: partTimeCareOrderPlanList[0],
        expectedServiceDays: null,
    },
    ...(isMockApiMode && {
        initialType: CARE_ORDER_BABY_TYPE.PART_TIME,
    }),
}, setApiProcessingPool, setApiDonePool);
export function useGetCareOrderPlanOptionList({ setApiProcessingPool, setApiDonePool, }) {
    const [careOrderPlanOptionList, setCareOrderPlanOptionList] = useState([]);
    const _id = useSelector(selectLiffId);
    const careOrderBabyForm = useSelector(selectCareOrderBabyForm);
    const partTimeCareOrderPlanList = useSelector(selectPartTimeCareOrderPlanList);
    const servedItem = careOrderBabyForm.servedItem;
    const symptom = careOrderBabyForm.symptom;
    const hospitalBed = careOrderBabyForm.hospitalBed;
    useEffect(() => {
        (async () => {
            const shouldRun = partTimeCareOrderPlanList && servedItem && symptom && hospitalBed;
            if (!shouldRun)
                return;
            const careOrderData = {
                servedItem,
                symptom,
                hospitalBed,
            };
            // STEP.1
            // Fetch timeSlotFee of allDay careOrderPlan and partTime careOrderPlans
            const promiseList = [];
            promiseList.push(fetchAllDayCareOrderTimeSlotFee({
                _id,
                careOrderData,
                setApiProcessingPool,
                setApiDonePool,
            }));
            promiseList.push(fetchPartTimeCareOrderTimeSlotFee({
                _id,
                careOrderData,
                partTimeCareOrderPlanList,
                setApiProcessingPool,
                setApiDonePool,
            }));
            const timeSlotFeeList = (await Promise.all(promiseList)).map((res) => res.data);
            const allDayCareOrderTimeSlotFee = timeSlotFeeList[0];
            const partTimeCareOrderTimeSlotFee = timeSlotFeeList[1];
            const allDayCareOrderDailyFee = get(allDayCareOrderTimeSlotFee, 'dailyFee');
            const partTimeCareOrderHourlyFee = get(partTimeCareOrderTimeSlotFee, 'hourlyFee');
            // STEP.2
            // Generate careOrderPlanOptionList
            const allDayCareOrderPlanOptionList = getAllDayCareOrderPlanOptionList({
                allDayCareOrderDailyFee,
                symptom,
            });
            const partTimeCareOrderPlanOptionList = getPartTimeCareOrderPlanOptionList({
                partTimeCareOrderPlanList,
                partTimeCareOrderHourlyFee,
                symptom,
            });
            const newCareOrderPlanOptionList = [
                ...allDayCareOrderPlanOptionList,
                ...partTimeCareOrderPlanOptionList,
            ];
            // STEP.3
            // update careOrderPlanOptionList
            setCareOrderPlanOptionList(newCareOrderPlanOptionList);
        })();
    }, [partTimeCareOrderPlanList, servedItem, symptom, hospitalBed]);
    return { careOrderPlanOptionList };
}
